import { Button, HStack, Text } from '@chakra-ui/react';
import { Table as TableType } from '@tanstack/react-table';
import * as _ from 'lodash';
import React from 'react';
import { v4 as uuidV4 } from 'uuid';

import {
  finleyAssert,
  error as commonError,
} from '@finley-technologies/common';

import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

export const getPagesToShow = (
  currentPageNumber: number,
  pageCount: number,
) => {
  finleyAssert(
    _.isInteger(currentPageNumber) && _.isInteger(pageCount),
    new commonError.FinleyError(
      `Expected currentPageNumber [${currentPageNumber}] and pageCount [${pageCount}] to be integers`,
    ),
  );

  finleyAssert(
    _.isFinite(currentPageNumber) &&
      _.isFinite(currentPageNumber) &&
      currentPageNumber <= pageCount &&
      currentPageNumber >= 1,
    new commonError.FinleyError(
      `Expected valid page number (between 1 and [${pageCount}]), but received [${currentPageNumber}]`,
    ),
  );

  if (pageCount <= 10) {
    return _.range(1, pageCount + 1);
  }

  const initialPages = [
    1,
    currentPageNumber - 1,
    currentPageNumber,
    currentPageNumber + 1,
    pageCount,
  ];

  const pagesV2 = initialPages.filter((page) => page >= 1 && page <= pageCount);

  const uniqueSortedPages = _.uniq(pagesV2).sort((a, b) => a - b);

  const pages: Array<number | '...'> = [];
  uniqueSortedPages.forEach((currPageNumber, index) => {
    pages.push(currPageNumber);
    if (index < uniqueSortedPages.length - 1) {
      const nextPage = uniqueSortedPages[index + 1];
      if (nextPage - currPageNumber === 2) {
        pages.push(currPageNumber + 1);
      } else if (nextPage - currPageNumber > 2) {
        pages.push('...');
      }
    }
  });

  return pages;
};

type TanStackPaginationControlProps = { table: TableType<any> };
export const TanStackPaginationControl = ({
  table,
}: TanStackPaginationControlProps) => {
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;
  if (pageCount === 0) {
    // its possible that there are no pages
    return null;
  }
  return (
    <HStack w="100%" spacing="8px" justifyContent="center" p="32px 0">
      <Button
        leftIcon={<FaIcon icon="chevron-left" />}
        colorScheme="gray"
        variant="v2-ghost"
        isDisabled={!table.getCanPreviousPage()}
        onClick={() => table.previousPage()}
      >
        Prev
      </Button>
      {getPagesToShow(currentPage, pageCount).map((pageNumber) =>
        typeof pageNumber === 'number' ? (
          <Button
            colorScheme="gray"
            isActive={currentPage === pageNumber}
            variant="v2-ghost"
            onClick={() => table.setPageIndex(pageNumber - 1)}
            key={_.isNumber(pageNumber) ? pageNumber : uuidV4()}
          >
            {pageNumber}
          </Button>
        ) : (
          <Text key={uuidV4()}>...</Text>
        ),
      )}
      <Button
        rightIcon={<FaIcon icon="chevron-right" />}
        colorScheme="gray"
        variant="v2-ghost"
        onClick={() => table.nextPage()}
        isDisabled={!table.getCanNextPage()}
      >
        Next
      </Button>
    </HStack>
  );
};
